import React from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { ArrowRight, ExternalLink, Mail, Phone, MapPin, Clock, Sparkles, Target, Zap } from 'lucide-react';
import { SITE } from '../../data/menu';

const ICONS = { Target, Zap, Sparkles };

// Shared dialog wrapper — sized for content, cinematic dark card.
const PanelShell = ({ open, onOpenChange, station, children }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      className="cinematic-scope w-[calc(100vw-24px)] max-w-4xl max-h-[90dvh] overflow-y-auto overscroll-contain bg-popover/95 border-border/80 text-foreground backdrop-blur-xl shadow-glow-red p-5 md:p-6"
      data-testid={`station-dialog-${station?.id || 'x'}`}
    >
      <DialogHeader className="text-left">
        <div className="text-primary text-xs uppercase tracking-[0.3em] font-medium mb-2 font-display">
          {station?.tagline}
        </div>
        <DialogTitle className="font-display text-3xl md:text-4xl text-foreground">{station?.label}</DialogTitle>
        <DialogDescription className="text-muted-foreground">{station?.blurb}</DialogDescription>
      </DialogHeader>
      <div className="mt-4">{children}</div>
    </DialogContent>
  </Dialog>
);

const SolutionsPanel = ({ station, onOpenChange }) => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-3 gap-4">
      {station.cards.map((c) => (
        <div
          key={c.title}
          className="rounded-xl p-5 glass hover-lift"
          data-testid={`solution-card-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        >
          <h3 className="font-display text-lg text-foreground mb-1">{c.title}</h3>
          <p className="text-primary text-xs uppercase tracking-wider mb-3">{c.subtitle}</p>
          <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
        </div>
      ))}
    </div>
    <div className="flex justify-end pt-2">
      <Link to={station.href} onClick={() => onOpenChange(false)}>
        <Button variant="hero" size="lg" data-testid="solutions-open-page-btn">
          Explore Solutions
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </Link>
    </div>
  </div>
);

const WorkPanel = ({ station, onOpenChange }) => (
  <div className="space-y-4">
    <ul className="divide-y divide-border/60 rounded-xl overflow-hidden glass">
      {station.caseStudies.map((cs) => (
        <li key={cs.slug}>
          <Link
            to={`/portfolio/${cs.slug}`}
            onClick={() => onOpenChange(false)}
            className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-secondary/40 transition-colors group"
            data-testid={`case-study-${cs.slug}`}
          >
            <div>
              <p className="text-primary text-[10px] uppercase tracking-[0.28em] mb-1">{cs.category}</p>
              <h4 className="font-display text-lg text-foreground group-hover:text-tertiary transition-colors">
                {cs.title}
              </h4>
              <p className="text-sm text-muted-foreground">{cs.subtitle}</p>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </Link>
        </li>
      ))}
    </ul>
    <div className="flex justify-end pt-2">
      <Link to={station.href} onClick={() => onOpenChange(false)}>
        <Button variant="hero" size="lg" data-testid="work-view-all-btn">
          View all case studies
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </Link>
    </div>
  </div>
);

const CompanyPanel = ({ station, onOpenChange }) => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-3 gap-4">
      {station.values.map((v) => {
        const Icon = ICONS[v.icon] || Sparkles;
        return (
          <div
            key={v.title}
            className="rounded-xl p-5 glass hover-lift"
            data-testid={`company-value-${v.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
          >
            <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center mb-3">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-display text-lg text-foreground mb-1">{v.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{v.body}</p>
          </div>
        );
      })}
    </div>
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-primary font-medium mb-3">
        Trusted by ambitious businesses worldwide
      </p>
      <div className="flex flex-wrap gap-2">
        {station.clients.map((c) => (
          <span
            key={c}
            className="px-3 py-1 text-xs rounded-full glass text-muted-foreground"
          >
            {c}
          </span>
        ))}
      </div>
    </div>
    <div className="flex justify-end pt-2">
      <Link to={station.href} onClick={() => onOpenChange(false)}>
        <Button variant="hero" size="lg" data-testid="company-about-btn">
          About Radius
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </Link>
    </div>
  </div>
);

const BlogPanel = ({ station, onOpenChange }) => (
  <div className="space-y-4">
    <ul className="divide-y divide-border/60 rounded-xl overflow-hidden glass">
      {station.posts.map((p) => (
        <li key={p.slug}>
          <a
            href={`${SITE.baseUrl}/blog/${p.slug}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-secondary/40 transition-colors group"
            data-testid={`blog-link-${p.slug}`}
          >
            <div>
              <p className="text-primary text-[10px] uppercase tracking-[0.28em] mb-1">
                {p.category} <span className="text-muted-foreground">· {p.readTime}</span>
              </p>
              <h4 className="font-display text-base text-foreground group-hover:text-tertiary transition-colors">
                {p.title}
              </h4>
            </div>
            <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
          </a>
        </li>
      ))}
    </ul>
    <div className="flex justify-end pt-2">
      <Link to={station.href} onClick={() => onOpenChange(false)}>
        <Button variant="hero" size="lg" data-testid="blog-read-btn">
          Read the blog
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </Link>
    </div>
  </div>
);

const ResourcesPanel = ({ station, onOpenChange }) => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-3 gap-4">
      {station.highlights.map((c) => (
        <div
          key={c.title}
          className="rounded-xl p-5 glass hover-lift"
          data-testid={`resource-highlight-${c.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        >
          <h3 className="font-display text-lg text-foreground mb-2">{c.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
        </div>
      ))}
    </div>
    <div className="flex justify-end pt-2">
      <Link to={station.href} onClick={() => onOpenChange(false)}>
        <Button variant="hero" size="lg" data-testid="resources-open-page-btn">
          Open Resource Library
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </Link>
    </div>
  </div>
);

const ContactPanel = ({ station, onOpenChange }) => {
  const d = station.details;
  const tiles = [
    { icon: Mail, label: 'Email', value: d.email, href: `mailto:${d.email}` },
    { icon: Phone, label: 'Phone', value: d.phone, href: d.phoneHref },
    { icon: MapPin, label: 'Location', value: d.location },
    { icon: Clock, label: 'Hours', value: d.hours.join(' \u00b7 ') },
  ];
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-3">
        {tiles.map(({ icon: Icon, label, value, href }) => {
          const Body = (
            <>
              <div className="w-10 h-10 rounded-lg bg-primary/15 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-1">{label}</p>
                <p className="text-sm text-foreground">{value}</p>
              </div>
            </>
          );
          return href ? (
            <a key={label} href={href} className="flex items-center gap-3 rounded-xl p-4 glass hover-lift">
              {Body}
            </a>
          ) : (
            <div key={label} className="flex items-center gap-3 rounded-xl p-4 glass">
              {Body}
            </div>
          );
        })}
      </div>
      <div className="flex justify-end pt-2 gap-3">
        <Link to={station.href} onClick={() => onOpenChange(false)}>
          <Button variant="hero" size="lg" data-testid="contact-lets-chat-btn">
            Connect
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export const ItemPanel = ({ open, onOpenChange, station }) => {
  if (!station) return null;
  return (
    <PanelShell open={open} onOpenChange={onOpenChange} station={station}>
      {station.id === 'solutions' && <SolutionsPanel station={station} onOpenChange={onOpenChange} />}
      {station.id === 'work' && <WorkPanel station={station} onOpenChange={onOpenChange} />}
      {station.id === 'company' && <CompanyPanel station={station} onOpenChange={onOpenChange} />}
      {station.id === 'blog' && <BlogPanel station={station} onOpenChange={onOpenChange} />}
      {station.id === 'resources' && <ResourcesPanel station={station} onOpenChange={onOpenChange} />}
      {station.id === 'contact' && <ContactPanel station={station} onOpenChange={onOpenChange} />}
    </PanelShell>
  );
};

export default ItemPanel;
